import React from "react";
import { Music, Phone, User, Mail, Calendar, Search } from "lucide-react";
import { formatDate } from "../../utils/dateFormat";
import {
  KaraokeQuickActions,
  PendingPaymentsPanel,
  BookingsTable,
  KaraokeStatistics,
  SpecialBookingForm,
} from "../../components/admin";

const AdminKaraokeTab = ({
  activeSubView,
  setActiveSubView,
  pendingBookings,
  bookings,
  confirmedBookings,
  filteredBookings,
  searchQuery,
  setSearchQuery,
  showSpecialBookingForm,
  setShowSpecialBookingForm,
  bookingsPagination,
  bookingsLoading,
  onVerifyPayment,
  onCancelPendingBooking,
  onCancelPaidBooking,
  onChangeDate,
  onAssignKJ,
  onApproveSwap,
  onRejectSwap,
  canCancelBooking,
  onPageChange,
  onSearchChange,
  onKjFilterChange,
  onStatusFilterChange,
  onSortChange,
  onFetchData,
  onAutoCompletePast,
}) => {
  return (
    <div className="space-y-6">
      {/* Quick Actions */}
      <KaraokeQuickActions
        activeSubView={activeSubView}
        setActiveSubView={setActiveSubView}
        pendingCount={pendingBookings.length}
        onSpecialBooking={() => { setShowSpecialBookingForm(true); setActiveSubView('special'); }}
      />

      {/* Pending Payments Panel */}
      {activeSubView === 'pending' && (
        <PendingPaymentsPanel
          pendingBookings={pendingBookings}
          onVerifyPayment={onVerifyPayment}
          onCancelBooking={onCancelPendingBooking}
          canCancelBooking={canCancelBooking}
          facilityType="karaoke"
        />
      )}

      {/* All Bookings Panel */}
      {activeSubView === 'confirmed' && (
        <div className="brutal-card">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
            <h3 className="text-xl font-bold flex items-center gap-2">
              <Music className="text-purple-500" /> All Karaoke Bookings
            </h3>
            <p className="text-xs text-gray-500">
              ⏰ Bookings auto-complete at 10:01 PM on booking day
            </p>
          </div>
          
          <BookingsTable
            bookings={bookings}
            onVerifyPayment={onVerifyPayment}
            onCancelPaid={onCancelPaidBooking}
            onCancelPending={onCancelPendingBooking}
            onChangeDate={onChangeDate}
            onAssignKJ={onAssignKJ}
            onApproveSwap={onApproveSwap}
            onRejectSwap={onRejectSwap}
            canCancelBooking={canCancelBooking}
            facilityType="karaoke"
            pagination={bookingsPagination}
            onPageChange={onPageChange}
            onSearchChange={onSearchChange}
            onKjFilterChange={onKjFilterChange}
            onStatusFilterChange={onStatusFilterChange}
            onSortChange={onSortChange}
            isLoading={bookingsLoading}
          />
        </div>
      )}

      {/* Customer Contacts Panel */}
      {activeSubView === 'members' && (
        <div className="brutal-card">
          <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
            <Phone className="text-blue-500" /> Customer Contacts
          </h3>
          
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="text"
              placeholder="Search members..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border-2 border-black"
            />
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredBookings
              .filter(b => b.status !== 'cancelled')
              .slice(0, 30)
              .map(booking => (
                <div key={booking.booking_id} className="border-2 border-black p-4 bg-white">
                  <div className="flex justify-between items-start mb-3">
                    <span className="font-mono font-bold text-purple-600 notranslate" translate="no">{booking.booking_code}</span>
                    <span className={`px-2 py-0.5 text-xs font-bold rounded ${
                      booking.status === 'paid' ? 'bg-green-100 text-green-700' :
                      booking.status === 'ongoing' ? 'bg-blue-100 text-blue-700' :
                      booking.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                      booking.status === 'completed' ? 'bg-purple-100 text-purple-700' :
                      'bg-gray-100 text-gray-700'
                    }`}>
                      {booking.status?.toUpperCase()}
                      {booking.booking_type === 'special' && ' (SPECIAL)'}
                    </span>
                  </div>
                  
                  <div className="space-y-2 mb-4">
                    <div className="flex items-center gap-2">
                      <User size={16} className="text-gray-400" />
                      <span className="font-bold notranslate" translate="no">{booking.member_name}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Mail size={16} className="text-gray-400" />
                      <a href={`mailto:${booking.member_email}`} className="text-sm text-blue-600 hover:underline truncate notranslate" translate="no">
                        {booking.member_email || 'No email'}
                      </a>
                    </div>
                    <div className="flex items-center gap-2">
                      <Calendar size={16} className="text-gray-400" />
                      <span className="text-sm font-mono">{formatDate(booking.date)}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Music size={16} className="text-purple-500" />
                      <span className="text-sm">{booking.slot_type === 'afternoon' ? '1PM-5PM' : '6PM-10PM'} • ${booking.price}</span>
                    </div>
                    {booking.kj_name && (
                      <div className="flex items-center gap-2">
                        <User size={16} className="text-purple-400" />
                        <span className="text-xs text-purple-600 font-bold">KJ: {booking.kj_name}</span>
                      </div>
                    )}
                  </div>
                  
                  {booking.member_phone ? (
                    <a 
                      href={`tel:${booking.member_phone}`}
                      className="flex items-center justify-center gap-2 w-full py-3 bg-green-500 text-white font-bold text-sm border-2 border-black hover:bg-green-600 transition-all"
                    >
                      <Phone size={18} /> CALL <span className="notranslate" translate="no">{booking.member_phone}</span>
                    </a>
                  ) : (
                    <div className="flex items-center justify-center gap-2 w-full py-3 bg-gray-300 text-gray-600 font-bold text-sm border-2 border-black cursor-not-allowed">
                      <Phone size={18} /> No Phone Number
                    </div>
                  )}
                </div>
              ))}
          </div>
          
          {filteredBookings.filter(b => b.status !== 'cancelled').length === 0 && (
            <p className="text-gray-500 text-center py-8">No member contacts found</p>
          )}
        </div>
      )}

      {/* Special Booking Form */}
      {activeSubView === 'special' && showSpecialBookingForm && (
        <SpecialBookingForm
          onSuccess={() => { onFetchData(); }}
          onClose={() => { setShowSpecialBookingForm(false); setActiveSubView(null); }}
        />
      )}

      {/* Karaoke Statistics */}
      <KaraokeStatistics
        bookings={bookings}
        pendingBookings={pendingBookings}
        confirmedBookings={confirmedBookings}
        onAutoCompletePast={onAutoCompletePast}
      />
    </div>
  );
};

export default AdminKaraokeTab;
